/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

'use strict';

function FileUploadController(
    this: JS5Object,

    // external deps
    _: JS5Object,
    FileItem: JS5Object,
    FileUploader: any,

    // internal deps
    AuthFactory: JS5Object,
    EndPointService: JS5Object,
    ModalService: JS5Object,
    FileUploadService: JS5Object,
) {

    const makeFileExtensionsLowerCase = (): void => {
        _.forEach(this.fileExtensions, (value: string, index: number): void => {
            this.fileExtensions.splice(index, 1, value.toLowerCase());
        });
    };

    const showMessageModal = (title: string, message: any): void => {
        const messageModalOpts = {
            messageText: message,
            pullCancelLeft: true,
            title: title,
            showOK: true,
            okText: 'Close',
            showCancel: false,
            windowClass: 'confirm-modal-sm',
        };
        ModalService.showMessageModal(messageModalOpts);
    };


    //===================//
    // File Upload Impl. //
    //===================//

    // Angular FileUploader hooks are prefixed with 'afu'.

    const afuWhenAddingFileFailed = (item: JS5Object, filter: JS5Object, options: object): void => {
        //
        // Note: the 'item' parameter is a "File" object, not a
        // "FileItem" object.
        //

        showMessageModal(
            item.name,
            `'${item.name}' could not be selected. It may not match the specified type, '${this.typeName}'.`
        );
    };

    const afuAfterAddingAll = (addedItems: JS5Object[]): void => {
        _.forEach(addedItems, (item: JS5Object): void => {
            if (!_.includes(_.map(this.selectedItems, 'file.name'), item.file.name)) {
                this.selectedItems.push(item);
            } else {
                showMessageModal(`Ignoring Duplicate`, `Ignoring ${item.file.name} as it is already in the upload list.`);
            }
        });
        // this.selectedItems = _.concat(this.selectedItems, addedItems);
    };

    const afuSuccessItem = (item: JS5Object, response: JS5Object, status: number, headers: JS5Object): void => {
        let uploadPath = response.path;

        // could be useful for support purposes to have the remote
        // upload path shown on the JS console
        console.info(`'${item.file.name}' uploaded to remote path: '${uploadPath}'`);

        this.completedItems.push({
            name: item.file.name,
            remotePath: uploadPath,
        });
    };

    const afuErrorItem = (item: JS5Object, response: string, status: number, headers: JS5Object): void => {
        let errorMsg: string[] = [`The server was unable to receive the file(s).`];

        if (status >= 400 && status < 500) {

            //
            // When an Nginx error occurs it sends a simple HTML page
            // in which the title contains the error message. This
            // attempts to parse the error out and return it.
            //

            errorMsg.push(`Response: "${$(response).filter('title').text()}"`);
        } else if (status > 0) {
            errorMsg.push(`(status: ${status})`);
        }

        showMessageModal('Upload Failed', errorMsg);
        this.resetUploader();
    };

    const afuProgressAll = (progress: number): void => {
        this.progress = progress;
        this.onUploadProgress({progress: progress});
    };

    const afuCompleteAll = (): void => {
        this.onUploadProgress({progress: 100});
        this.onFilesUploaded({uploadedFiles: this.completedItems});
    };

    const addFiltersToUploader = (): void => {
        this.uploaderConfig.filters.push({
            name: 'extensionFilter',
            fn: (item: JS5Object, options: JS5Object): boolean => {
                let fileName = _.get(item, 'name', null);
                if (fileName && _.includes(fileName, '.')) {
                    let fileExtension = _.reverse(fileName.split('.'))[0].toLowerCase();
                    if (_.includes(this.fileExtensions, fileExtension)) {
                        return true;
                    }
                }
                return false;
            },
        });
    };

    const createFileUploader = (): JS5Object => {
        let uploaderConfig = new FileUploader();

        uploaderConfig.url = EndPointService.fileUploadEndPoint;
        uploaderConfig.alias = 'multipart-content';
        uploaderConfig.autoUpload = false;
        uploaderConfig.removeAfterUpload = true;
        uploaderConfig.isHTML5 = true;
        uploaderConfig.formData = {};
        uploaderConfig.headers = {
            'Authorization': AuthFactory.getAuthHeader(),
            'Accept': 'application/json, text/plain, */*',
        };

        // set callback hooks
        uploaderConfig.onWhenAddingFileFailed = afuWhenAddingFileFailed;
        uploaderConfig.onAfterAddingAll = afuAfterAddingAll;
        uploaderConfig.onSuccessItem = afuSuccessItem;
        uploaderConfig.onErrorItem = afuErrorItem;
        uploaderConfig.onProgressAll = afuProgressAll;
        uploaderConfig.onCompleteAll = afuCompleteAll;

        return uploaderConfig;
    };


    //================//
    // Template Logic //
    //================//

    this.getSelectedNames = (): void => {
        return _.map(this.selectedItems, 'file.name').join(', ');
    };

    this.beginUpload = (): void => {
        this.completedItems = [];
        console.info(`uploading ${this.getSelectedNames()}`);
        this.uploaderConfig.uploadAll();
    };

    this.resetUploader = (): void => {
        this.selectedItems = [];
        this.completedItems = [];
        this.progress = 0;

        this.uploaderConfig.cancelAll();
        this.uploaderConfig.clearQueue();

    };

    this.canUpload = (): boolean => this.selectedItems.length > 0;
    this.canReset = (): boolean => this.selectedItems.length > 0;


    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = (): void => {
        makeFileExtensionsLowerCase();

        this.selectedItems = [];
        this.completedItems = [];
        this.progress = 0;

        this.uploaderConfig = createFileUploader();
        addFiltersToUploader();

        FileUploadService.beginUpload = this.beginUpload;
        FileUploadService.resetUploader = this.resetUploader;
        FileUploadService.canUpload = this.canUpload;
        FileUploadService.canReset = this.canReset;

        this.onUploaderInitialized();
    };


}

angular
.module('prism-ui.components')
.component('fileUpload', {
    controller: FileUploadController,
    templateUrl: 'components/fileUpload/file-upload-template.html',
    bindings: {

        //
        // Bindings:
        //
        //      fileExtensions - an array of strings specifying
        //          valid file extensions for this uploader,
        //          e.g. 'PNG' or 'xlsx'. Case insensitive.
        //
        //      typeName - a user-friendly string describing the
        //          type(s) of files accepted by this uploader,
        //          e.g. 'Image (.png, .gif, .jpg)' or
        //          'Excel Spreadsheet (.xlsx)'.
        //
        //      onUploadProgress - a callback that will be invoked
        //          when the upload progress has changed. The
        //          `progress` property ranges from 0 to 100.
        //
        //          const onUploadProgress = (progress: number): void => { /*...*/ };
        //
        //      onFilesUploaded - a callback that will be invoked
        //          when all selected files have completed
        //          uploading. `uploadedFiles` is an array listing
        //          all files that were successfully uploaded.
        //
        //          const onFilesUploaded = (uploadedFiles: JS5Object[]): void => { /*...*/ };
        //
        //          Each item in `uploadedFiles` is an object:
        //
        //          {
        //              name: <the file's name>,
        //              remotePath: <the file's remote path on the server>
        //          }
        //

        fileExtensions: '<',
        typeName: '<',
        onUploaderInitialized: '&',
        onFilesUploaded: '&',
        onUploadProgress: '&',
    }
});
